import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function Hostel() {
  const qc = useQueryClient();
  const { data: rooms } = useQuery({ queryKey: ['hostel-rooms'], queryFn: () => api.get('/hostel/rooms').then((r) => r.data) });
  const { data: allocs } = useQuery({ queryKey: ['hostel-allocs'], queryFn: () => api.get('/hostel/allocations').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const [rform, setRform] = useState({ name: '', capacity: '4', floor: '', warden_name: '' });
  const [aform, setAform] = useState({ student_id: '', room_id: '', bed_no: '' });
  const inval = () => { qc.invalidateQueries({ queryKey: ['hostel-rooms'] }); qc.invalidateQueries({ queryKey: ['hostel-allocs'] }); };
  const roomMut = useMutation({
    mutationFn: () => api.post('/hostel/rooms', { ...rform, capacity: parseInt(rform.capacity, 10) || 4 }),
    onSuccess: () => { toast.success('Room added'); setRform({ name: '', capacity: '4', floor: '', warden_name: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const editRoomMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/hostel/rooms/${id}`, patch),
    onSuccess: () => { toast.success('Room updated'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const delRoomMut = useMutation({
    mutationFn: (id) => api.delete(`/hostel/rooms/${id}`),
    onSuccess: () => { toast.success('Room removed'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const allocMut = useMutation({
    mutationFn: () => api.post('/hostel/allocations', aform),
    onSuccess: () => { toast.success('Bed allocated'); setAform({ student_id: '', room_id: '', bed_no: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const checkoutMut = useMutation({
    mutationFn: (id) => api.post(`/hostel/allocations/${id}/checkout`),
    onSuccess: () => { toast.success('Checked out - bed freed'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const editRoom = (r) => {
    const warden_name = window.prompt('Warden name:', r.warden_name || '') ?? r.warden_name;
    const capacity = parseInt(window.prompt('Capacity:', r.capacity), 10) || r.capacity;
    editRoomMut.mutate({ id: r.id, patch: { warden_name, capacity } });
  };

  return (
    <div data-testid="hostel-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Hostel</h1>
      <p className="text-sm text-slate-500 mb-6">Rooms, bed allocation, wardens & check-in / check-out</p>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="rooms-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Rooms & occupancy</p>
          {(rooms || []).map((r) => (
            <div key={r.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2 flex-wrap" data-testid={`room-row-${r.id}`}>
              <span className="font-medium">{r.name}</span>
              <span className="text-xs text-slate-400">{r.floor && `${r.floor} · `}warden {r.warden_name || '-'}</span>
              <span className="ms-auto flex items-center gap-2">
                <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${r.free_beds === 0 ? 'bg-rose-100 text-rose-700' : 'bg-green-100 text-green-800'}`} data-testid={`room-occupancy-${r.id}`}>{r.occupied}/{r.capacity} beds</span>
                <button data-testid={`room-edit-${r.id}`} onClick={() => editRoom(r)} className="text-xs underline text-orange-700">edit</button>
                <button data-testid={`room-delete-${r.id}`} onClick={() => window.confirm(`Remove room ${r.name}?`) && delRoomMut.mutate(r.id)} className="text-xs underline text-slate-300 hover:text-rose-600">remove</button>
              </span>
            </div>
          ))}
          {(rooms || []).length === 0 && <p className="text-sm text-slate-400 py-2">No rooms yet.</p>}
          <div className="grid grid-cols-2 gap-2 mt-3 border-t border-slate-100 pt-3">
            <Input data-testid="room-name-input" placeholder="Room (A-101)" value={rform.name} onChange={(e) => setRform({ ...rform, name: e.target.value })} />
            <Input data-testid="room-capacity-input" type="number" placeholder="Beds" value={rform.capacity} onChange={(e) => setRform({ ...rform, capacity: e.target.value })} />
            <Input data-testid="room-warden-input" placeholder="Warden" value={rform.warden_name} onChange={(e) => setRform({ ...rform, warden_name: e.target.value })} />
            <Button data-testid="room-add-button" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!rform.name || roomMut.isPending} onClick={() => roomMut.mutate()}>Add room</Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="allocations-panel">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Residents (active)</p>
          {(allocs || []).map((a) => (
            <div key={a.id} className="py-2 border-t border-slate-100 text-sm flex items-center gap-2" data-testid={`alloc-row-${a.id}`}>
              <span className="font-medium">{a.student_name}</span>
              <span className="text-xs text-slate-400">Gr {a.grade} · {a.room_name}{a.bed_no ? ` bed ${a.bed_no}` : ''}</span>
              <button data-testid={`alloc-checkout-${a.id}`} onClick={() => checkoutMut.mutate(a.id)} className="ms-auto text-xs border border-slate-200 rounded px-2 py-1 hover:border-rose-500 hover:text-rose-600">Check out</button>
            </div>
          ))}
          {(allocs || []).length === 0 && <p className="text-sm text-slate-400 py-2">No residents checked in.</p>}
          <div className="grid grid-cols-2 gap-2 mt-3 border-t border-slate-100 pt-3">
            <select data-testid="alloc-student-select" value={aform.student_id} onChange={(e) => setAform({ ...aform, student_id: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              <option value="">Student...</option>
              {(students || []).filter((s) => s.status === 'active').map((s) => <option key={s.id} value={s.id}>{s.name} - {s.grade}{s.section}</option>)}
            </select>
            <select data-testid="alloc-room-select" value={aform.room_id} onChange={(e) => setAform({ ...aform, room_id: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              <option value="">Room...</option>
              {(rooms || []).map((r) => <option key={r.id} value={r.id}>{r.name} ({r.free_beds} free)</option>)}
            </select>
            <Input data-testid="alloc-bed-input" placeholder="Bed no (optional)" value={aform.bed_no} onChange={(e) => setAform({ ...aform, bed_no: e.target.value })} />
            <Button data-testid="alloc-button" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!aform.student_id || !aform.room_id || allocMut.isPending} onClick={() => allocMut.mutate()}>Allocate bed</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
